import { EngineState, UI_EVENTS } from 'playable/dist/src/constants';
import React from 'react';
import ReactDOM from 'react-dom';
import { ModuleUI } from './components/ModuleUI';
import { createSettingsButtonContainer } from './components/SettingsButton/SettingsButton';
import { createSettingsPopoverContainer } from './components/SettingsPopover/SettingsPopover';
import { defaultTexts } from './useTexts';
const QUALITIES = [240, 360, 480, 720, 1080, 1440, 2160, 4320];
export class SettingsModule {
    constructor(dependencies) {
        this.dependencies = dependencies;
        this.isFullScreen = false;
        this.mainUIVisible = false;
        this.config = {};
        this.qualities = [];
        this.dummyContainer = document.createElement('div');
        this.settingsPopover = createSettingsPopoverContainer();
        this.settingsButton = createSettingsButtonContainer();
        this.setQualities = () => {
            const adapter = this.getPlaybackAdapter();
            const hls = adapter?.hls;
            if (!hls) {
                return;
            }
            this.qualities = hls.levels.map((level, index) => ({
                level: index,
                name: this.getQualityName(this.getLevelHeight(level)),
            }));
            this.qualities.push(this.getAutoQuality());
            this.render();
        };
        this.handleManifestParsed = () => {
            this.setQualities();
        };
        this.handleLevelSwitched = () => {
            this.selectedQuality = this.getSelectedQuality();
            this.render();
        };
        this.handleMetaDataLoaded = () => {
            // hls is available after METADATA_LOADED event
            const adapter = this.getPlaybackAdapter();
            const hls = adapter?.hls;
            if (!hls) {
                return;
            }
            // Use strings to avoid importing full Hls.js. Shaking doesn't work
            hls.on('hlsLevelSwitched', this.handleLevelSwitched);
            hls.on('hlsManifestParsed', this.handleManifestParsed);
            this.setQualities();
            this.selectedQuality = this.getSelectedQuality();
            this.render();
        };
        this.initSettings = (config) => {
            const { rootContainer, bottomBlock, tooltipService } = this.dependencies;
            this.config = config;
            rootContainer.appendComponentElement(this.settingsPopover.element);
            bottomBlock.addControl('settings', this.settingsButton.element);
            tooltipService.createReference(this.settingsButton.element, {
                text: config.texts?.buttonTooltip || defaultTexts.buttonTooltip,
            });
            this.render();
        };
        this.handleSpeedChange = (rate) => {
            const { onSpeedChange } = this.config;
            this.dependencies.engine.setPlaybackRate(rate);
            if (onSpeedChange) {
                onSpeedChange(rate);
            }
        };
        this.handleFullScreenChanged = (isFullScreen) => {
            this.isFullScreen = isFullScreen;
            this.render();
        };
        this.handleMainUIVisibility = (visible) => {
            this.mainUIVisible = visible;
            this.render();
        };
        this.setMainUIAlwaysShow = (visible) => {
            this.dependencies.mainUIBlock.setShouldAlwaysShow(visible);
        };
        this.handleQualityChange = (quality) => {
            const { onQualityChange } = this.config;
            const adapter = this.getPlaybackAdapter();
            const hls = adapter?.hls;
            this.selectedQuality = quality;
            if (hls) {
                hls.nextLevel = quality.level;
            }
            if (onQualityChange) {
                const biQualityName = quality.level === -1 ? 'auto' : quality.name;
                onQualityChange(biQualityName);
            }
            this.render();
        };
        const { eventEmitter } = dependencies;
        eventEmitter.on(EngineState.METADATA_LOADED, this.handleMetaDataLoaded);
        eventEmitter.on(UI_EVENTS.FULL_SCREEN_STATE_CHANGED, this.handleFullScreenChanged);
        eventEmitter.on(UI_EVENTS.MAIN_BLOCK_HIDE, () => this.handleMainUIVisibility(false));
        eventEmitter.on(UI_EVENTS.MAIN_BLOCK_SHOW, () => this.handleMainUIVisibility(true));
    }
    getPlaybackAdapter() {
        return this.dependencies.nativeOutput.attachedAdapter;
    }
    getAutoQuality() {
        return {
            level: -1,
            name: this.config.texts?.autoQualityTitle || defaultTexts.autoQualityTitle,
        };
    }
    getSelectedQuality() {
        const adapter = this.getPlaybackAdapter();
        const hls = adapter?.hls;
        if (!hls) {
            return;
        }
        const { currentLevel, autoLevelEnabled } = hls;
        if (autoLevelEnabled) {
            return this.getAutoQuality();
        }
        const level = hls.levels[currentLevel];
        return {
            level: currentLevel,
            name: this.getQualityName(level.height),
        };
    }
    getAPI() {
        return {
            initSettings: this.initSettings,
        };
    }
    getLevelHeight(level) {
        if (level.height) {
            return level.height;
        }
        if (level.url[0]) {
            // live streams cannot provide RESOLUTION in m3u8 file, so rely labels on path
            // 'https://prod-live-europe-west1.wixmp.com/live_streams/wixmp-3dbd5cb16bb136182e099f78_vUpZ2E9b/wixmp-3dbd5cb16bb136182e099f78_vUpZ2E9b_res720p/index.m3u8'
            const matchedHeight = level.url[0].match(/(live_streams.*_res)([0-9]+)/)?.[2];
            if (matchedHeight) {
                return +matchedHeight;
            }
        }
        return 0;
    }
    getQualityName(height) {
        for (const quality of QUALITIES) {
            if (height <= quality) {
                return quality + 'p';
            }
        }
        return height + 'p';
    }
    render() {
        this.settingsPopover.updatePosition(this.isFullScreen);
        ReactDOM.render(React.createElement(ModuleUI, { buttonContainer: this.settingsButton.element, popoverContainer: this.settingsPopover.element, isFullScreen: this.isFullScreen, config: this.config, mainUIVisible: this.mainUIVisible, selectedQuality: this.selectedQuality, onPopoverToggle: this.setMainUIAlwaysShow, onSpeedChange: this.handleSpeedChange, onQualityChange: this.handleQualityChange, qualities: this.qualities }), this.dummyContainer);
    }
}
SettingsModule.dependencies = [
    'bottomBlock',
    'eventEmitter',
    'rootContainer',
    'engine',
    'tooltipService',
    'nativeOutput',
    'mainUIBlock',
];
//# sourceMappingURL=module.js.map